import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '@liminal/brand/semantic';
import { withOpacity } from '@liminal/brand/color-utils';
import { FONT_STACK_BODY, FONT_STACK_DISPLAY } from '@liminal/brand/font-faces';
import { borderRadius, fontSizes, fontWeights, letterSpacing, lineHeights, spacing } from '@liminal/brand/scale';
import {
  PARTNER_COPY,
  earnLabel,
  formatLilText,
  galleryRatio,
  isDecimalText,
  payFromLabel,
  payLabel,
} from '@liminal/content/rewards';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { LogoMark } from '@liminal/web-ui/brand/generated/LogoMark';
import type { RewardMerchant_Category } from '@/api/generated';
import { merchantCategory } from '@/components/ui/statusHelpers';
import { payLevelLil } from '@/lib/rewards';

// The partner as the mobile app draws it, for the partner form: its card on
// the Rewards page, and its partner page. A web replica of
// application/mobile/src/features/rewards/components/ExperienceOfferCard.tsx
// and ExperienceDetailBody.tsx, at the app's sizes in points, from the app's
// own tokens (`colors` is brand's mobile-faithful map) and its own words
// (@liminal/content/rewards). Change those components and this one together.

/** The phone's width in points: the app's layout is drawn at this size, then fitted to the column. */
export const PHONE_WIDTH = 375;
/** How much of the partner page shows before it scrolls, as on a phone. */
const PHONE_SCREEN_HEIGHT = 720;
/** The card's size in the app: every partner is a card in its category's sideways row. */
const CARD_WIDTH = 260;
const CARD_HEIGHT = 300;
/** The partner's logo in the card's bottom-right corner: the same size on every card. */
const LOGO_SIZE = 40;
/** The badge's icon, sized to its small caps. */
const BADGE_ICON_SIZE = 12;
/** The frosted badge's blur, in px: enough to read over any photo. */
const BADGE_BLUR = 15;
/** The numbered disc beside each step, and the pin disc beside each place. */
const DISC_SIZE = 32;
const PIN_ICON_SIZE = 18;
/** The Liminal mark on a partner page with no photos. */
const MARK_SIZE = 96;
/** The gallery's dots: the photo showing is bright and a touch larger. */
const DOT_SIZE = spacing.sm;
const RESTING_DOT_OPACITY = 0.48;
const ACTIVE_DOT_SCALE = 1.25;
/** A link button's height and tracking, as the app's small secondary Button. */
const LINK_BUTTON_HEIGHT = 36;
const LINK_BUTTON_TRACKING = 0.5;
const LINK_BUTTON_BORDER = 2;

/** The app's `spacing.smMd`, which brand's scale does not carry: the chip's and a small button's side padding. */
const SM_MD = 12;

/** An empty field shows this in grey, so it never reads as copy about to ship. */
const PLACEHOLDER_NAME = 'Partner name';

export interface PreviewImage {
  readonly uri: string;
  readonly width?: number;
  readonly height?: number;
}

export interface PreviewLocation {
  readonly label: string;
  readonly address: string;
}

export interface PreviewLink {
  readonly label: string;
}

export interface PartnerAppPreviewProps {
  readonly view: 'card' | 'page';
  readonly displayName: string;
  readonly description: string;
  /** The photos in order, the main one first. */
  readonly images: readonly PreviewImage[];
  /** Where the logo is served from, or null when the partner has none: the card then shows nothing there. */
  readonly logoUri: string | null;
  readonly category: RewardMerchant_Category | '';
  readonly earnLilPerUsd: string;
  readonly redeemLilPerUsd: string;
  /**
   * The Pay with LIL levels' USD as typed, one per row; empty, or all rows
   * empty, at a partner without levels. With levels the app prices Pay with
   * LIL from the first level's LIL instead of per dollar.
   */
  readonly payLevels: readonly string[];
  readonly locations: readonly PreviewLocation[];
  readonly links: readonly PreviewLink[];
}

/**
 * The partner's card or page inside a phone-shaped frame, laid out at the
 * phone's width and scaled down to fit a narrower column, so lines break
 * where they break in the app.
 */
export function PartnerAppPreview(props: PartnerAppPreviewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const element = ref.current;
    // jsdom has no ResizeObserver; the preview then stays at full size.
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setZoom(Math.min(1, entry.contentRect.width / PHONE_WIDTH));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <Box
        data-testid="partner-app-preview"
        sx={{
          zoom,
          width: PHONE_WIDTH,
          mx: 'auto',
          borderRadius: `${borderRadius['2xl']}px`,
          border: `1px solid ${colors.border.medium}`,
          overflow: 'hidden',
          bgcolor: colors.background.primary,
        }}
      >
        <Box sx={{ height: PHONE_SCREEN_HEIGHT, overflowY: 'auto' }}>
          {props.view === 'card' ? (
            // The partner's row on the Rewards page: its category's name, then its card.
            <Box sx={{ py: `${spacing.lg}px`, display: 'flex', flexDirection: 'column', gap: `${spacing.sm}px` }}>
              <Box data-testid="partner-preview-row-title" sx={{ px: `${spacing.lg}px` }}>
                <SectionHeading>
                  {props.category === '' ? PARTNER_COPY.morePartners : merchantCategory(props.category).label}
                </SectionHeading>
              </Box>
              <Box sx={{ px: `${spacing.lg}px` }}>
                <PartnerCardPreview {...props} />
              </Box>
            </Box>
          ) : (
            <PartnerPagePreview {...props} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

const display = (weight: number, size: number, color: string, lineHeight?: number) => ({
  fontFamily: FONT_STACK_DISPLAY,
  fontWeight: weight,
  fontSize: size,
  lineHeight: lineHeight ?? 'normal',
  color,
});

/** Text the operator has not given yet, in grey. */
function Placeholder({ children }: { readonly children: ReactNode }) {
  return <Box component="span" sx={{ color: colors.text.tertiary, opacity: 0.6 }}>{children}</Box>;
}

/** A rate as the app writes it, or a grey stand-in while the field is not yet a decimal. */
function rateText(rate: string, label: (rate: string) => string, stand: string): ReactNode {
  const trimmed = rate.trim();
  return isDecimalText(trimmed) ? label(trimmed) : <Placeholder>{stand}</Placeholder>;
}

/**
 * The first level's LIL at the redeem rate, as the app's "Pay from" reads it:
 * undefined at a partner without levels, null while the level or the rate is
 * not a decimal yet, so the caller shows a stand-in rather than a made-up price.
 */
function firstLevelLil(payLevels: readonly string[], redeemLilPerUsd: string): string | null | undefined {
  if (!payLevels.some((usd) => usd.trim() !== '')) return undefined;
  return payLevelLil(payLevels[0] ?? '', redeemLilPerUsd);
}

const clamp = (lines: number) => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
});

/** ExperienceOfferCard, as a row's card: the partner's main photo, the badge, the name and its two rates, and its logo in the corner. */
function PartnerCardPreview({ displayName, images, logoUri, earnLilPerUsd, redeemLilPerUsd, payLevels }: PartnerAppPreviewProps) {
  const name = displayName.trim();
  const main = images[0];
  const fromLil = firstLevelLil(payLevels, redeemLilPerUsd);
  return (
    <Box
      data-testid="partner-preview-card"
      sx={{
        position: 'relative',
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        borderRadius: `${borderRadius.xl}px`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        bgcolor: colors.surface.secondary,
      }}
    >
      {main ? (
        <Box component="img" src={main.uri} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        // The app covers the card with the Liminal mark until the partner has a photo.
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LogoMark size={CARD_HEIGHT} />
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(${withOpacity(colors.brand.black, 0)} 30%, ${withOpacity(colors.brand.black, 0.8)} 100%)`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: spacing.md,
          left: spacing.md,
          display: 'flex',
          alignItems: 'center',
          gap: `${spacing.xs}px`,
          px: `${spacing.sm}px`,
          py: `${spacing.xs}px`,
          borderRadius: `${borderRadius.full}px`,
          border: `1px solid ${withOpacity(colors.brand.white, 0.24)}`,
          bgcolor: withOpacity(colors.brand.black, 0.48),
          backdropFilter: `blur(${BADGE_BLUR}px)`,
        }}
      >
        <Icon name="gift-outline" size={BADGE_ICON_SIZE} color={colors.text.inverse} />
        <Box
          component="span"
          sx={{
            fontFamily: FONT_STACK_BODY,
            fontSize: fontSizes.xs,
            color: colors.text.inverse,
            textTransform: 'uppercase',
            letterSpacing: `${letterSpacing.wide}px`,
          }}
        >
          {PARTNER_COPY.badge}
        </Box>
      </Box>
      {/* The copy and the logo side by side, so a long name wraps before it reaches the logo. */}
      <Box sx={{ position: 'relative', p: `${spacing.lg}px`, display: 'flex', alignItems: 'flex-end', gap: `${spacing.md}px` }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: `${spacing.sm}px` }}>
          <Box sx={{ ...display(fontWeights.medium, fontSizes['2xl'], colors.text.inverse, lineHeights.tight), ...clamp(2) }}>
            {name || PLACEHOLDER_NAME}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: `${spacing.sm}px` }}>
            <Box
              sx={{
                px: `${spacing.sm}px`,
                py: `${spacing.xs}px`,
                borderRadius: `${borderRadius.full}px`,
                bgcolor: colors.surface.primary,
                ...display(fontWeights.semibold, fontSizes.sm, colors.text.primary),
              }}
            >
              {rateText(earnLilPerUsd, earnLabel, 'Earn … LIL per $1')}
            </Box>
            <Box
              sx={{
                flexShrink: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...display(fontWeights.regular, fontSizes.sm, withOpacity(colors.text.inverse, 0.8)),
              }}
            >
              {fromLil === undefined ? (
                rateText(redeemLilPerUsd, payLabel, 'or pay … LIL per $1')
              ) : fromLil === null ? (
                <Placeholder>Pay from … LIL</Placeholder>
              ) : (
                payFromLabel([{ usd: (payLevels[0] ?? '').trim(), lil: fromLil }])
              )}
            </Box>
          </Box>
        </Box>
        {logoUri !== null && (
          <Box
            component="img"
            src={logoUri}
            alt=""
            data-testid="partner-preview-logo"
            sx={{
              width: LOGO_SIZE,
              height: LOGO_SIZE,
              flexShrink: 0,
              objectFit: 'cover',
              borderRadius: `${borderRadius.md}px`,
              border: `1px solid ${withOpacity(colors.brand.white, 0.24)}`,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

/** ExperienceDetailBody: the gallery, the name, chip and description, the rates, How it works, Getting there and Links. */
function PartnerPagePreview({
  displayName,
  description,
  images,
  category,
  earnLilPerUsd,
  redeemLilPerUsd,
  payLevels,
  locations,
  links,
}: PartnerAppPreviewProps) {
  const name = displayName.trim();
  const fromLil = firstLevelLil(payLevels, redeemLilPerUsd);
  const facts = [
    { label: PARTNER_COPY.earnFact, value: rateText(earnLilPerUsd, formatLilText, '… LIL') },
    fromLil === undefined
      ? { label: PARTNER_COPY.payFact, value: rateText(redeemLilPerUsd, formatLilText, '… LIL') }
      : { label: PARTNER_COPY.payFromFact, value: fromLil === null ? <Placeholder>… LIL</Placeholder> : formatLilText(fromLil) },
  ];
  const shownLocations = locations.filter((location) => location.label.trim() !== '');
  const shownLinks = links.filter((link) => link.label.trim() !== '');
  const main = images[0];
  const frameHeight =
    PHONE_WIDTH * galleryRatio(main === undefined ? undefined : { width: main.width ?? 0, height: main.height ?? 0 });

  return (
    <Box data-testid="partner-preview-page">
      <Box sx={{ position: 'relative', width: PHONE_WIDTH, height: frameHeight, bgcolor: colors.surface.secondary }}>
        {main ? (
          <Box component="img" src={main.uri} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LogoMark size={MARK_SIZE} />
          </Box>
        )}
        {images.length > 1 && (
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: spacing.md, display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                gap: `${spacing.sm}px`,
                px: `${spacing.sm}px`,
                py: `${spacing.xs}px`,
                borderRadius: `${borderRadius.full}px`,
                bgcolor: colors.overlay.base[32],
              }}
            >
              {images.map((image, index) => (
                <Box
                  key={image.uri}
                  sx={{
                    width: DOT_SIZE,
                    height: DOT_SIZE,
                    borderRadius: `${borderRadius.full}px`,
                    bgcolor: colors.text.inverse,
                    opacity: index === 0 ? 1 : RESTING_DOT_OPACITY,
                    transform: index === 0 ? `scale(${ACTIVE_DOT_SCALE})` : 'none',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ p: `${spacing.lg}px`, display: 'flex', flexDirection: 'column', gap: `${spacing.lg}px` }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.sm}px` }}>
          <Box sx={display(fontWeights.bold, fontSizes['4xl'], colors.text.primary, lineHeights.tight)}>
            {name || <Placeholder>{PLACEHOLDER_NAME}</Placeholder>}
          </Box>
          {category !== '' && (
            <Box
              data-testid="partner-preview-category"
              sx={{
                alignSelf: 'flex-start',
                px: `${SM_MD}px`,
                py: `${spacing.xs}px`,
                borderRadius: `${borderRadius.full}px`,
                bgcolor: colors.surface.secondary,
                ...display(fontWeights.medium, fontSizes.sm, colors.text.secondary),
              }}
            >
              {merchantCategory(category).label}
            </Box>
          )}
          {description.trim() !== '' && (
            <Box sx={display(fontWeights.regular, fontSizes.md, colors.text.secondary, lineHeights.normal)}>{description.trim()}</Box>
          )}
        </Box>

        <Box sx={{ display: 'flex' }}>
          {facts.map((fact, index) => (
            <Box
              key={fact.label}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: `${spacing.xs}px`,
                px: `${spacing.sm}px`,
                borderLeft: index > 0 ? `1px solid ${colors.border.medium}` : 'none',
              }}
            >
              <Box sx={{ ...display(fontWeights.semibold, fontSizes.xl, colors.text.primary), whiteSpace: 'nowrap' }}>{fact.value}</Box>
              <Box sx={{ ...display(fontWeights.regular, fontSizes.sm, colors.text.tertiary), whiteSpace: 'nowrap' }}>{fact.label}</Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.md}px` }}>
          <SectionHeading>{PARTNER_COPY.howItWorks}</SectionHeading>
          {PARTNER_COPY.howItWorksSteps(name || PLACEHOLDER_NAME).map((step, index) => (
            <Box key={step.title} sx={{ display: 'flex', gap: `${spacing.md}px` }}>
              <Disc>
                <Box sx={display(fontWeights.semibold, fontSizes.md, colors.brand.orange)}>{index + 1}</Box>
              </Disc>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: `${spacing.xs}px` }}>
                <Box sx={display(fontWeights.semibold, fontSizes.lg, colors.text.primary)}>{step.title}</Box>
                <Box sx={display(fontWeights.regular, fontSizes.sm, colors.text.secondary, lineHeights.normal)}>{step.body}</Box>
              </Box>
            </Box>
          ))}
          <Box sx={display(fontWeights.regular, fontSizes.sm, colors.text.tertiary, lineHeights.normal)}>{PARTNER_COPY.earnInstead}</Box>
        </Box>

        {shownLocations.length > 0 && (
          <Box data-testid="partner-preview-locations" sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.md}px` }}>
            <SectionHeading>{PARTNER_COPY.locations}</SectionHeading>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.sm}px` }}>
              {shownLocations.map((location, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: `${spacing.md}px`,
                    p: `${spacing.md}px`,
                    borderRadius: `${borderRadius.lg}px`,
                    border: `1px solid ${colors.border.light}`,
                    bgcolor: colors.surface.primary,
                  }}
                >
                  <Disc>
                    <Icon name="location-outline" size={PIN_ICON_SIZE} color={colors.brand.orange} />
                  </Disc>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: `${spacing.xs}px` }}>
                    <Box sx={{ ...display(fontWeights.semibold, fontSizes.md, colors.text.primary), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {location.label.trim()}
                    </Box>
                    {location.address.trim() !== '' && (
                      <Box sx={{ ...display(fontWeights.regular, fontSizes.sm, colors.text.secondary, lineHeights.normal), ...clamp(2) }}>
                        {location.address.trim()}
                      </Box>
                    )}
                  </Box>
                  <Box sx={display(fontWeights.semibold, fontSizes.sm, colors.text.accent)}>{PARTNER_COPY.openInMaps}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {shownLinks.length > 0 && (
          <Box data-testid="partner-preview-links" sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.md}px` }}>
            <SectionHeading>{PARTNER_COPY.links}</SectionHeading>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${spacing.sm}px` }}>
              {shownLinks.map((link, index) => (
                <Box
                  key={index}
                  sx={{
                    height: LINK_BUTTON_HEIGHT,
                    px: `${SM_MD}px`,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: `${borderRadius.xl}px`,
                    border: `${LINK_BUTTON_BORDER}px solid ${colors.brand.orange}`,
                    bgcolor: colors.brand.white,
                    fontFamily: FONT_STACK_BODY,
                    fontSize: fontSizes.sm,
                    textTransform: 'uppercase',
                    letterSpacing: `${LINK_BUTTON_TRACKING}px`,
                    color: colors.text.secondary,
                  }}
                >
                  {link.label.trim()}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** The app's H3. */
function SectionHeading({ children }: { readonly children: ReactNode }) {
  return <Typography component="h3" sx={display(fontWeights.bold, fontSizes.xl, colors.text.primary, lineHeights.snug)}>{children}</Typography>;
}

/** The orange-tinted disc behind a step's number or a place's pin. */
function Disc({ children }: { readonly children: ReactNode }) {
  return (
    <Box
      sx={{
        width: DISC_SIZE,
        height: DISC_SIZE,
        flexShrink: 0,
        borderRadius: `${borderRadius.full}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: withOpacity(colors.brand.orange, 0.08),
      }}
    >
      {children}
    </Box>
  );
}
